/**
 * SoilMyGroupsPage — the operator's soil application worklist (issue #417 S4,
 * rebuilt for Compact Mode in U5).
 *
 * Reads from the Dexie `soilMyGroups` cache via useLiveQuery so it works
 * offline, and triggers a silent background refresh plus a queue drain on
 * mount. Rows tap into the field page.
 *
 * THE SEAM: the navigate() path below must match App.tsx exactly. A mismatch
 * is a 404 in production that a mocked navigate will happily hide — the test
 * asserts the literal string.
 *
 * STYLING: this page uses the `.ntr-sg-*` namespace, NOT `.ntr-my-session-*`.
 * The latter is shared with the FOLIAR NutritionMySessionsPage, and no test in
 * this repo can see a CSS change — restyling it would silently alter foliar.
 *
 * Mounted behind <RequireLinkedOperator> in App.tsx.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db';
import type { CachedSoilMyGroup } from '../../db';
import { refreshMySoilGroups } from '../../services/soilGroupCacheService';
import {
  syncPendingSoilGroups, resetSoilGroupSyncState,
  readSoilGroupConflicts, dismissSoilGroupConflict,
  readPendingSoilGroupCount, readPendingSoilGroupKeys,
} from '../../services/soilGroupSyncService';
import PageHeader from '../shell/PageHeader';
import { Notice, RowCard, StatusPill } from '../patterns';
import { formatMeasure } from '../../utils/formatMeasure';
import { resolveSyncBanner } from './soilOperator';
import './nutrition.css';

type Conflict = { localId: number; label: string; groupId: string };

const STATUS_VARIANT: Record<string, 'info' | 'ok' | 'warn'> = {
  not_started: 'info', in_progress: 'warn', complete: 'ok', signed_off: 'ok',
};

const SoilMyGroupsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingKeys, setPendingKeys] = useState<string[]>([]);
  const [online, setOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  const rows: CachedSoilMyGroup[] =
    (useLiveQuery(() => db.soilMyGroups.toArray(), []) as CachedSoilMyGroup[] | undefined) ?? [];

  const loadConflicts = useCallback(async () => {
    setConflicts(await readSoilGroupConflicts());
  }, []);

  const loadPending = useCallback(async () => {
    const [count, keys] = await Promise.all([
      readPendingSoilGroupCount(),
      readPendingSoilGroupKeys(),
    ]);
    setPendingCount(count);
    setPendingKeys(keys);
  }, []);

  useEffect(() => {
    // Recover anything stuck mid-flight, drain, refresh, then surface conflicts
    // and whatever the drain could not clear. Each step is failure-tolerant so
    // an offline operator still gets a rendered page.
    resetSoilGroupSyncState()
      .then(() => syncPendingSoilGroups())
      .catch(() => {})
      .then(() => refreshMySoilGroups())
      .catch(() => {})
      .then(() => loadConflicts())
      .catch(() => {})
      .then(() => loadPending())
      .catch(() => {});
    // `t` from react-i18next is not referentially stable here; omit it deliberately
    // rather than "satisfying" the linter with an ever-changing dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConflicts, loadPending]);

  useEffect(() => {
    // The banner is the operator's only signal that work is being held locally,
    // so it must track connectivity live rather than only at mount.
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const onDismiss = async (localId: number) => {
    await dismissSoilGroupConflict(localId);
    await loadConflicts();
  };

  const banner = resolveSyncBanner(online, pendingCount);
  const bannerText =
    banner.kind === 'offlineIdle' ? t('nutrition.soil.myGroups.syncOfflineIdle')
      : banner.kind === 'offlineQueued' ? t('nutrition.soil.myGroups.syncOfflineQueued', { count: banner.count })
        : banner.kind === 'onlineQueued' ? t('nutrition.soil.myGroups.syncOnlineQueued', { count: banner.count })
          : '';

  return (
    <div className="pat-page" data-testid="soil-my-groups-page">
      <PageHeader title={t('nutrition.soil.myGroups.title')} />

      {banner.kind !== 'none' && (
        <Notice tone={banner.kind === 'offlineIdle' ? 'info' : 'warn'} testId="soil-sync-banner">
          {bannerText}
        </Notice>
      )}

      {conflicts.length > 0 && (
        <div data-testid="soil-group-conflict-banner">
          <Notice tone="warn">
            {t('nutrition.soil.myGroups.conflictBanner', {
              count: conflicts.length,
              groups: conflicts.map((c) => c.label).join(', '),
            })}
            {conflicts.map((c) => (
              <button
                key={c.localId}
                type="button"
                className="pat-btn"
                data-testid={`soil-group-conflict-dismiss-${c.localId}`}
                onClick={() => onDismiss(c.localId)}
              >
                {t('nutrition.soil.myGroups.dismiss')}
              </button>
            ))}
          </Notice>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="ntr-empty">
          <div className="ntr-empty__message">{t('nutrition.soil.myGroups.empty')}</div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3 mt-3">
          {rows.map((row) => (
            <div
              key={row.id}
              data-testid={`soil-group-row-${row.id}`}
              className="ntr-sg-row"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/nutrition/soil-groups/${row.sessionId}/${row.groupId}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate(`/nutrition/soil-groups/${row.sessionId}/${row.groupId}`);
                }
              }}
            >
              {/* es5 target: indexOf, never Array.prototype.includes. */}
              <RowCard queued={pendingKeys.indexOf(row.id) !== -1}>
                <div className="ntr-sg-card">
                  <div className="ntr-sg-card__identity">{`${row.productName} · ${row.month}`}</div>
                  <div className="ntr-sg-card__label">{row.label}</div>
                  <div className="ntr-sg-chips">
                    {row.orchardNames.map((name) => (
                      <span
                        key={name}
                        className="ntr-sg-chip"
                        data-testid={`soil-group-chip-${row.id}-${name}`}
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                  <div className="ntr-sg-hero" data-testid={`soil-group-hero-${row.id}`}>
                    {/* formatMeasure with no unit is a plain 2dp number; the unit is a
                        separate span so the hero number can carry its own type scale. */}
                    <span className="ntr-sg-hero__value">{formatMeasure(row.plannedKg)}</span>
                    <span className="ntr-sg-hero__unit">{t('nutrition.soil.myGroups.kg')}</span>
                  </div>
                  {pendingKeys.indexOf(row.id) !== -1 && (
                    <div className="ntr-sg-card__queued" data-testid={`soil-group-queued-${row.id}`}>
                      {t('nutrition.soil.myGroups.queuedRow')}
                    </div>
                  )}
                </div>
                <div className="ntr-sg-card__rail">
                  <StatusPill variant={STATUS_VARIANT[row.status] ?? 'info'}>
                    {t(`nutrition.soil.groupStatus.${row.status}`)}
                  </StatusPill>
                </div>
              </RowCard>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SoilMyGroupsPage;
